class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          
          // Ensure product-card-variants components are properly initialized after dynamic content is loaded
          // Custom elements should auto-initialize, but we need to ensure scripts are loaded first
          // and give the browser time to process the new DOM
          this.#initializeDynamicComponents();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Initialize dynamic components after recommendations are loaded
   * This ensures product-card-variants and other custom elements work correctly
   */
  #initializeDynamicComponents() {
    // Wait for the next frame to ensure DOM is fully processed
    requestAnimationFrame(() => {
      // Check if product-card-variants custom element is defined
      // The script is loaded inline in the _product-card-variants block
      const productCardVariantsScript = this.querySelector('script[src*="product-card-variants.js"]');
      
      if (productCardVariantsScript) {
        // Script tag exists in the loaded HTML
        // Module scripts in innerHTML should execute, but if the custom element isn't defined yet,
        // we need to ensure the script loads properly
        if (!customElements.get('product-card-variants')) {
          // Custom element not defined yet, ensure the script executes
          // For module scripts, we need to create a new script element and append it
          const scriptUrl = productCardVariantsScript.src || productCardVariantsScript.getAttribute('src');
          if (scriptUrl) {
            // Check if script is already in the document head
            const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
            if (!existingScript) {
              // Script not loaded yet, load it now
              const script = document.createElement('script');
              script.type = 'module';
              script.src = scriptUrl;
              document.head.appendChild(script);
              
              // Wait for the custom element to be defined
              const checkInterval = setInterval(() => {
                if (customElements.get('product-card-variants')) {
                  clearInterval(checkInterval);
                  this.#finalizeInitialization();
                }
              }, 50);
              
              // Timeout after 2 seconds
              setTimeout(() => {
                clearInterval(checkInterval);
                this.#finalizeInitialization();
              }, 2000);
              return;
            }
          }
        }
      }
      
      // Custom element is already defined or will be defined by the inline script
      // Give it a moment to initialize
      this.#finalizeInitialization();
    });
  }

  /**
   * Finalize initialization after scripts are loaded
   */
  #finalizeInitialization() {
    // Give custom elements time to initialize their connectedCallback
    setTimeout(() => {
      // Trigger product card organization if the function exists
      if (typeof window.initProductCards === 'function') {
        window.initProductCards();
      }
      
      // Dispatch a custom event to notify that recommendations are ready
      this.dispatchEvent(new CustomEvent('recommendations-loaded', {
        bubbles: true,
        detail: { element: this }
      }));
    }, 100);
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
